import type React from 'react';
import { Stack } from '@mui/material';
import { useScreenSize } from '@/hooks/use-screen-size';
import List from './list';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';

import type { Mode } from '@/types/entity-mode';
import type { FlowDetailMode } from '../flow-detail.types';
import type { FlowSlotAssetsWideAnchor } from '../flow-detail-tabs/flow-slot-assets-wide-anchor';

export default function SlotList({
  setShowFlowDetails,
  setFlowDetailsDirection,
  onSlotSelect,
  onShowTimingAlert,
  onShowPublishAlert,
  modeOverride,
  readOnly = false,
  middleSectionOpensOverlayOnSmallScreen = false,
  startRowMediaOpenDetailsMode,
  onWideScreenSlotDeletedFromList,
  onSessionPublishSuccess,
  flowControlInlinePerform,
  flowControlPerformExpandedKey,
  onFlowControlPerformExpand,
  renderFlowControlPerformMiddle,
  renderFlowControlPerformMiddleFlow,
  onFlowControlStartItemMiddleClick,
  smallScreenInlineDetailsVariant,
  hideTimedUntimedSectionDivider,
  hideCollapsibleInfoAlert = false,
  compactInfoAlert = false,
  hideFlowStartSlotRow = false,
  hideFlowStartSlotRowWhenSmallScreen = false,
  hideEndItemMediaIndicator = false,
  showSlotDragHandle = false,
  slotListBodyInScrollContainer = false,
  flowSlotAssetsWideAnchor,
  onFlowSlotAssetsWideAnchorChange,
  hideEmptyUntimedSection = false,
  flowControlInlineMedia = false,
  rightSectionPlayIcon = false,
}: {
  setShowFlowDetails: React.Dispatch<React.SetStateAction<FlowDetailMode | undefined>>;
  setFlowDetailsDirection: React.Dispatch<React.SetStateAction<'up' | 'down' | undefined>>;
  onSlotSelect?: (slot: FlowSlotDto, isUserInitiated?: boolean) => void;
  onShowTimingAlert?: () => void;
  onShowPublishAlert?: () => void;
  modeOverride?: Mode;
  /** When true, hides mode switcher and disables editing/drag-drop (e.g. for organize column on large screens) */
  readOnly?: boolean;
  /** When true, clicking middle section on small screens opens overlay (e.g. Slot Assets tab) */
  middleSectionOpensOverlayOnSmallScreen?: boolean;
  startRowMediaOpenDetailsMode?: FlowDetailMode;
  /** When set (e.g. flow schedule tab), wide-screen inline slot delete calls this instead of navigating to flow settings */
  onWideScreenSlotDeletedFromList?: () => void;
  onSessionPublishSuccess?: () => void;
  flowControlInlinePerform?: boolean;
  flowControlPerformExpandedKey?: string | null;
  onFlowControlPerformExpand?: (key: string | null) => void;
  renderFlowControlPerformMiddle?: (
    slot: import('@/types/dtos/flows/response/flow.dto').FlowSlotDto,
  ) => React.ReactNode;
  renderFlowControlPerformMiddleFlow?: () => React.ReactNode;
  onFlowControlStartItemMiddleClick?: () => void;
  smallScreenInlineDetailsVariant?: 'slotAssets' | 'schedule';
  /** When true, no horizontal rule between untimed and timed slot blocks */
  hideTimedUntimedSectionDivider?: boolean;
  /** When true, CollapsibleInfoAlert is not rendered inside List */
  hideCollapsibleInfoAlert?: boolean;
  /** Schedule tab: borderless info alert without expand chevron (see `flow-schedule-tab.tsx`). */
  compactInfoAlert?: boolean;
  /** When true, the top flow-start row (time / event slot day) is omitted. Only wide `FlowScheduleTab` sets this. */
  hideFlowStartSlotRow?: boolean;
  /** When true, the flow-start row is also omitted on small viewports (schedule tab only; see `flow-schedule-tab.tsx`). */
  hideFlowStartSlotRowWhenSmallScreen?: boolean;
  /** When true, the end-of-flow row omits the media indicator (schedule tab only; see `flow-schedule-tab.tsx`). */
  hideEndItemMediaIndicator?: boolean;
  /** When true, draggable slots show a left-column drag handle; right column narrows (schedule tab only). */
  showSlotDragHandle?: boolean;
  /** Narrow schedule column: pin publication info above internal scroll (see `FlowSlotAssetsSlotListColumn`). */
  slotListBodyInScrollContainer?: boolean;
  flowSlotAssetsWideAnchor?: FlowSlotAssetsWideAnchor;
  onFlowSlotAssetsWideAnchorChange?: (anchor: FlowSlotAssetsWideAnchor) => void;
  /** Slot assets column only — see `FlowSlotAssetsSlotListColumn` */
  hideEmptyUntimedSection?: boolean;
  /** Control tab: click-to-expand draft media under rows */
  flowControlInlineMedia?: boolean;
  /** Control tab: inert play icon in slot right section */
  rightSectionPlayIcon?: boolean;
}) {
  const { isSmallScreen } = useScreenSize({ breakpoint: 860 });
  const stackSpacing =
    startRowMediaOpenDetailsMode === 'flowSlotAssets' || startRowMediaOpenDetailsMode === 'flowControl' || !readOnly
      ? 2
      : 1;

  return (
    <Stack
      spacing={stackSpacing}
      sx={{
        ...(flowControlInlinePerform
          ? { minHeight: 0, minWidth: 0, width: '100%' }
          : readOnly
            ? { minWidth: 0, width: '100%' }
            : {}),
        ...(isSmallScreen ? { pb: 4 } : {}),
        ...(slotListBodyInScrollContainer
          ? {
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              width: '100%',
              overflow: 'hidden',
            }
          : {}),
      }}
    >
      <List
        setShowFlowDetails={setShowFlowDetails}
        setFlowDetailsDirection={setFlowDetailsDirection}
        onSlotSelect={onSlotSelect}
        onShowTimingAlert={onShowTimingAlert}
        onShowPublishAlert={onShowPublishAlert}
        modeOverride={modeOverride}
        readOnly={readOnly}
        rightSectionOnlyPublishIcon={!readOnly}
        middleSectionOpensOverlayOnSmallScreen={middleSectionOpensOverlayOnSmallScreen}
        startRowMediaOpenDetailsMode={startRowMediaOpenDetailsMode}
        onWideScreenSlotDeletedFromList={onWideScreenSlotDeletedFromList}
        onSessionPublishSuccess={onSessionPublishSuccess}
        flowControlInlinePerform={flowControlInlinePerform}
        flowControlPerformExpandedKey={flowControlPerformExpandedKey}
        onFlowControlPerformExpand={onFlowControlPerformExpand}
        renderFlowControlPerformMiddle={renderFlowControlPerformMiddle}
        renderFlowControlPerformMiddleFlow={renderFlowControlPerformMiddleFlow}
        onFlowControlStartItemMiddleClick={onFlowControlStartItemMiddleClick}
        smallScreenInlineDetailsVariant={smallScreenInlineDetailsVariant}
        hideTimedUntimedSectionDivider={hideTimedUntimedSectionDivider}
        hideFlowStartSlotRow={hideFlowStartSlotRow}
        hideFlowStartSlotRowWhenSmallScreen={hideFlowStartSlotRowWhenSmallScreen}
        hideEndItemMediaIndicator={hideEndItemMediaIndicator}
        showSlotDragHandle={showSlotDragHandle}
        hideCollapsibleInfoAlert={hideCollapsibleInfoAlert}
        compactInfoAlert={compactInfoAlert}
        slotListBodyInScrollContainer={slotListBodyInScrollContainer}
        flowSlotAssetsWideAnchor={flowSlotAssetsWideAnchor}
        onFlowSlotAssetsWideAnchorChange={onFlowSlotAssetsWideAnchorChange}
        hideEmptyUntimedSection={hideEmptyUntimedSection}
        flowControlInlineMedia={flowControlInlineMedia}
        rightSectionPlayIcon={rightSectionPlayIcon}
      />
    </Stack>
  );
}
